<template>
  <div class="padded" style="text-align: left;">

    <filter-bar
      v-if="!hideTop"
      :title="title"
      :icon="icon"
      :search-placeholder="searchPlaceholder"
      :show-search="showSearch"
      :show-refresh="showRefresh"
      :table-loading="loadingEvent"
      :table-loaded="loadedEvent"
      :set-filter-event="filterEvent"
      :reset-filter-event="filterReset"
      :refresh-event="refreshTable">
      <!-- Forward the `actions` slot into filter-bar so add-buttons share the
           same row as title + search. Keeps the `<hr />` below at full width. -->
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </filter-bar>

    <!-- `below-filter` slot: sits between the filter-bar and the data rows.
         Mirrors the legacy vuetable-2 `bottom-comp` placement; used by
         SitesTable for the SiteMenu chip strip. -->
    <slot name="below-filter" />

    <el-table
      v-loading="isLoading"
      :ref="tableRef"
      :data="rows"
      :row-key="trackBy"
      stripe
      :empty-text="''"
      @sort-change="onSortChange">

      <!-- Leading chevron column: appears whenever a layer route or layer component is wired.
           Mirrors the vuetable-2 expand-row button so users muscle-memoried on
           the chevron keep working alongside the new Site-ID layer link. -->
      <el-table-column v-if="layerRoute || layerComponent" label="" width="70" align="center" class-name="xolas-table__chevron-col">
        <template slot-scope="scope">
          <el-button
            type="info"
            size="mini"
            plain
            round
            :aria-label="'Open detail for row ' + (scope.$index + 1)"
            @click="onRowClick(scope.row, $event)">
            <i class="fa fa-angle-right"></i>
          </el-button>
        </template>
      </el-table-column>

      <el-table-column
        v-for="field in visibleFields"
        :key="field.name"
        :prop="field.name.startsWith('__') ? undefined : field.name"
        :label="field.title"
        :sortable="field.sortField ? 'custom' : false"
        :class-name="field.dataClass"
        :label-class-name="field.titleClass"
        :width="field.width"
        :min-width="field.minWidth"
        :align="field.align">
        <template slot-scope="scope">
          <slot
            v-if="field.name.startsWith('__slot:')"
            :name="field.name.split(':')[1]"
            :row-data="scope.row"
            :row-index="scope.$index" />
          <span v-else-if="field.name === '__sequence'">{{ sequenceIndex(scope.$index) }}</span>
          <!-- eslint-disable-next-line vue/no-v-html -- callback output is developer-controlled HTML (vuetable-2 parity) -->
          <span v-else-if="field.callback" v-html="formatCell(field, scope.row)"></span>
          <a
            v-else-if="(layerRoute || layerComponent) && field === firstDataField"
            :href="resolveLayerHref(scope.row)"
            class="xolas-table__row-link"
            @click.prevent="onRowClick(scope.row, $event)"
            @mousedown="onRowMouseDown(scope.row, $event)">
            {{ formatCell(field, scope.row) }}
          </a>
          <span v-else>{{ formatCell(field, scope.row) }}</span>
        </template>
      </el-table-column>

      <el-table-column v-if="showEditButton || showDeleteButton" label="" width="120">
        <template slot-scope="scope">
          <el-button v-if="showEditButton" size="mini" @click="$emit('edit-click', scope.row)">Edit</el-button>
          <el-button v-if="showDeleteButton" type="danger" size="mini" @click="$emit('delete-click', scope.row)">Delete</el-button>
        </template>
      </el-table-column>

      <template #empty>
        <slot name="empty">
          <span class="text-muted">No records</span>
        </slot>
      </template>

    </el-table>

    <el-row v-if="showPagination && page.total > page.per_page">
      <el-col align="right" :span="24">
        <xolas-table-pagination
          ref="pagination"
          :page-data="page"
          @change-page="onChangePage" />
      </el-col>
    </el-row>

  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import ArrayMixins from '@/mixins/ArrayMixins'
import DateMixins from '@/mixins/DateMixins'
import FilterBar from '@/components/atoms/tables/DataTableFilterBar'
import XolasTablePagination from '@/components/atoms/tables/XolasTablePagination'
import { pushDrawer, resolveDrawerHref } from '@/lib/layerNav'

export default {
  name: 'xolas-table',

  mixins: [ArrayMixins, DateMixins],

  components: {
    FilterBar,
    XolasTablePagination
  },

  props: {
    url: {
      type: String,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    tableRef: {
      type: String,
      default: 'xolastable'
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    sortField: {
      type: [String, Array],
      default: 'id'
    },
    sortDirection: {
      type: String,
      default: 'asc'
    },
    perPage: {
      type: Number,
      default: 10
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showRefresh: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    hideTop: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: () => ({})
    },
    // layer wiring (legacy — deprecated in XOL-209 PR 2, use layerComponent instead)
    layerRoute: {
      type: String,
      default: null
    },
    layerParam: {
      type: String,
      default: 'id'
    },
    layerParamName: {
      type: String,
      default: 'id'
    },
    // layer wiring (XOL-209 PR 2 — d.<component>.<args> URL grammar)
    // layerComponent: registered drawer name (e.g. 'bill', 'site')
    layerComponent: {
      type: String,
      default: null
    },
    // layerArgs: rowData-key map per drawer arg (e.g. { id: 'id' })
    layerArgs: {
      type: Object,
      default: function () { return { id: 'id' } }
    },
    // category-B affordances — off by default in PR 1
    showEditButton: {
      type: Boolean,
      default: false
    },
    showDeleteButton: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      rows: [],
      isLoading: true,
      page: {
        total: 0,
        current_page: 1,
        last_page: 1,
        per_page: this.perPage
      },
      filterText: '',
      sortOrder: [{ field: this.sortField, direction: this.sortDirection }],
      moreParams: Object.assign({}, this.params)
    }
  },

  computed: {
    visibleFields () {
      return this.fields.filter(f => f.visible !== false)
    },

    firstDataField () {
      // First data field eligible for the layer-link <a>: skip __-prefixed
      // specials (e.g. __sequence, __slot:*). Callback fields are allowed —
      // they may format the value but are still valid click targets (e.g.
      // SiteUtilTable's 'date' field). Only the __-prefix check is applied.
      return this.fields.find(f => !f.name.startsWith('__'))
    },

    loadedEvent () {
      return this.tableRef + '-loaded'
    },

    loadingEvent () {
      return this.tableRef + '-loading'
    },

    refreshTable () {
      return this.tableRef + '-refresh-table'
    },

    filterEvent () {
      return this.tableRef + '-filter-set'
    },

    filterReset () {
      return this.tableRef + '-filter-reset'
    }
  },

  methods: {
    fetch () {
      this.isLoading = true
      this.$events.emit(this.loadingEvent)

      const sortField = this.sortOrder[0] ? this.sortOrder[0].field : this.sortField
      const sortDir = this.sortOrder[0] ? this.sortOrder[0].direction : this.sortDirection

      const params = Object.assign({}, this.moreParams, {
        page: this.page.current_page,
        per_page: this.page.per_page,
        sort: sortField,
        sort_dir: sortDir
      })

      if (this.filterText) {
        params.filter = this.filterText
      }

      // Match legacy DataTable: resolve the API base + org scoping via the
      // global Vue mixin's getEndpoint(). Without this the request hits the
      // SPA origin (e.g. localhost:8080/sites) and returns index.html, which
      // parses to an empty data set with no error surfaced.
      const apiUrl = typeof this.getEndpoint === 'function'
        ? this.getEndpoint(this.url)
        : this.url

      axios.get(apiUrl, { params }).then(res => {
        const data = res.data
        this.rows = data.data || []
        this.page = {
          total: data.total || 0,
          current_page: data.current_page || 1,
          last_page: data.last_page || 1,
          per_page: data.per_page || this.perPage
        }
        this.$emit('total-changed', this.page.total)
        this.isLoading = false
        this.$events.emit(this.loadedEvent)
        this.$emit('loaded', this.rows)
      }).catch(err => {
        this.isLoading = false
        this.$events.emit(this.loadedEvent)
        this.$emit('error', err)
      })
    },

    refresh () {
      this.fetch()
    },

    resolveLayerHref (rowData) {
      if (this.layerComponent) {
        const args = {}
        for (const [argName, rowKey] of Object.entries(this.layerArgs)) {
          args[argName] = rowData[rowKey]
        }
        return resolveDrawerHref(this.$router, { open: { [this.layerComponent]: args } })
      }
      // deprecated: layerRoute path
      if (!this.layerRoute) return '#'
      const resolved = this.$router.resolve({
        name: this.layerRoute,
        params: { [this.layerParamName]: rowData[this.layerParam] }
      })
      return resolved.href
    },

    onRowMouseDown (rowData, event) {
      if (!this.layerRoute && !this.layerComponent) return
      const isNewTab = event.metaKey || event.ctrlKey || event.button === 1
      if (isNewTab) {
        event.preventDefault()
        window.open(this.resolveLayerHref(rowData), '_blank')
      }
    },

    onRowClick (rowData, event) {
      this.$emit('row-click', rowData)
      if (this.layerComponent) {
        const args = {}
        for (const [argName, rowKey] of Object.entries(this.layerArgs)) {
          args[argName] = rowData[rowKey]
        }
        pushDrawer(this.$router, { open: { [this.layerComponent]: args } })
      } else if (this.layerRoute) {
        // deprecated: layerRoute path — kept for one PR cycle
        this.$router.push({
          name: this.layerRoute,
          params: { [this.layerParamName]: rowData[this.layerParam] }
        })
      }
    },

    onSortChange ({ column, prop, order }) {
      if (!prop) return
      this.sortOrder = [{
        field: prop,
        direction: order === 'ascending' ? 'asc' : 'desc'
      }]
      this.fetch()
    },

    onChangePage (page) {
      this.page.current_page = page
      this.fetch()
    },

    onFilterSet (filterText) {
      this.filterText = filterText
      this.page.current_page = 1
      this.fetch()
    },

    onFilterReset () {
      this.filterText = ''
      this.moreParams = Object.assign({}, this.params)
      this.page.current_page = 1
      this.fetch()
    },

    sequenceIndex (rowIndex) {
      // 1-based, page-aware (matches vuetable-2 __sequence semantics)
      const offset = (this.page.current_page - 1) * this.page.per_page
      return offset + rowIndex + 1
    },

    formatCell (field, row) {
      // Resolve nested property path (e.g. 'tenancy.utilAccount')
      const value = field.name.split('.').reduce((obj, key) => {
        return obj && obj[key] !== undefined ? obj[key] : null
      }, row)

      if (!field.callback) return value

      if (typeof field.callback === 'function') {
        return field.callback(value)
      }

      if (typeof field.callback === 'string') {
        const parts = field.callback.split('|')
        const methodName = parts[0]
        const arg = parts[1]
        if (typeof this[methodName] === 'function') {
          return arg ? this[methodName](value, arg) : this[methodName](value)
        }
      }

      return value
    }
  },

  watch: {
    // Keep internal sortOrder in sync with reactive sort-field/sort-direction
    // props. data() initializes sortOrder ONCE; without this watcher the
    // fetch() fallback `this.sortOrder[0] ? ... : this.sortField` always picks
    // the stale initial sortOrder. RequestTable changes :sort-field on the fly
    // via its sort dropdown — this watcher makes that change actually take.
    // refresh() is intentionally NOT called here; consumers own when to refetch.
    sortField (val) {
      this.sortOrder = [{ field: val, direction: this.sortDirection }]
    },
    sortDirection (val) {
      this.sortOrder = [{ field: this.sortField, direction: val }]
    }
  },

  mounted () {
    this.$events.on(this.filterEvent, this.onFilterSet)
    this.$events.on(this.filterReset, this.onFilterReset)
    this.$events.on(this.refreshTable, this.refresh)
    this.fetch()
  },

  beforeDestroy () {
    this.$events.off(this.filterEvent, this.onFilterSet)
    this.$events.off(this.filterReset, this.onFilterReset)
    this.$events.off(this.refreshTable, this.refresh)
  }
}
</script>

<style scoped>
.xolas-table__row-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.xolas-table__row-link:hover {
  text-decoration: underline;
  color: #409eff;
}

/* Chevron column uses Element UI's built-in
   `el-button--info el-button--mini is-plain is-round` classes (matches the
   legacy vuetable-2 expand-row button). No custom dimensions needed —
   Element UI handles spacing, color, and border via type/size/plain/round. */

</style>
